import { call } from 'redux-saga/effects';
import { ROUTES } from '@shared/constants';
import { resendVerificationEmail } from '@services/cognitoUserService';
import {
  isCognitoPasswordResetRequiredError,
  isCognitoUserNotConfirmedError,
} from '@services/cognitoErrorNormalizer';
import {
  canAutoResendEmailVerification,
  markAutoResentEmailVerification,
} from '@services/cognitoEmailVerificationService';
import { LOCAL_STORAGE_ITEM, setToLocalStorage } from '@utils/localStorageHelper';
import { normalizeCognitoSignInErrorMessage } from './normalizeSignInError';
import { stopSignInAndRedirect } from '../authSharedSagas';
import { CHALLENGE_SESSION_STORAGE_KEYS } from '@services/cognito/constants';
import { normalizeCodeDeliveryDetails } from '../shared/codeDeliveryDetails';

const getCognitoErrorCode = (err) => err?.code || err?.name || err?.__type;
const getCognitoErrorMessage = (err) => err?.message || '';

const getCognitoErrorDetails = (err) => ({
  code: getCognitoErrorCode(err),
  message: getCognitoErrorMessage(err),
});

const ERROR_MESSAGE_PATTERNS = {
  emailNotVerified: /EmailNotVerified/i,
  passwordResetRequired: /password reset required/i,
};

const isEmailNotVerifiedError = (err) => {
  const { code, message } = getCognitoErrorDetails(err);
  return code === 'UserLambdaValidationException' && ERROR_MESSAGE_PATTERNS.emailNotVerified.test(message);
};

const isUserNotConfirmedError = (err) => isCognitoUserNotConfirmedError(err);

const isPasswordResetRequiredError = (err) => {
  if (isCognitoPasswordResetRequiredError(err)) return true;
  const { message } = getCognitoErrorDetails(err);
  return ERROR_MESSAGE_PATTERNS.passwordResetRequired.test(message);
};

function* attemptAutoResendVerificationCode(email) {
  const shouldAutoResend = canAutoResendEmailVerification(email);
  if (!shouldAutoResend) {
    return { autoResent: false, sendError: null, codeDeliveryDetails: null };
  }

  const resendResult = yield call(resendVerificationEmail, email);
  const autoResent = Boolean(resendResult?.success);
  if (autoResent) {
    markAutoResentEmailVerification(email);
    return {
      autoResent: true,
      sendError: null,
      codeDeliveryDetails: normalizeCodeDeliveryDetails(resendResult?.result?.CodeDeliveryDetails),
    };
  }

  return { autoResent: false, sendError: resendResult?.error || null, codeDeliveryDetails: null };
}

function* redirectToVerifyEmailSent(stopSignInAndRedirect, { email }) {
  const { autoResent, sendError, codeDeliveryDetails } = yield* attemptAutoResendVerificationCode(email);
  yield* stopSignInAndRedirect(ROUTES.VERIFY_EMAIL_SENT, {
    email,
    autoResent,
    sendError,
    codeDeliveryDetails,
  });
}

export function* handleCognitoSignInFailure({ email, err, stopSignInAndRedirect }) {
  if (isEmailNotVerifiedError(err)) {
    yield* redirectToVerifyEmailSent(stopSignInAndRedirect, {
      email,
    });
    return true;
  }

  if (isUserNotConfirmedError(err)) {
    yield* redirectToVerifyEmailSent(stopSignInAndRedirect, {
      email,
    });
    return true;
  }

  if (isPasswordResetRequiredError(err)) {
    yield* stopSignInAndRedirect(ROUTES.RESET_PASSWORD, {
      email,
      reason: 'passwordResetRequired',
    });
    return true;
  }

  return false;
}

export function* handleCognitoSignInNonSuccess({ email, cognitoResult }) {
  const challenge = cognitoResult?.challenge;
  if (challenge === 'NEW_PASSWORD_REQUIRED') {
    setToLocalStorage(LOCAL_STORAGE_ITEM.COGNITO_NEW_PASSWORD_CHALLENGE, cognitoResult?.payload);
    yield* stopSignInAndRedirect(ROUTES.RESET_PASSWORD, {
      reason: 'passwordExpired',
    });
    return { handled: true };
  }

  // Handle MFA challenge
  if (
    challenge === 'SMS_MFA' ||
    challenge === 'SOFTWARE_TOKEN_MFA' ||
    challenge === 'EMAIL_OTP' ||
    challenge === 'CUSTOM_CHALLENGE'
  ) {
    const challengeParameters =
      cognitoResult?.payload?.challengeParameters || cognitoResult?.ChallengeParameters || {};
    const session = cognitoResult?.payload?.session || cognitoResult?.Session;
    const usernameForChallenge = challengeParameters?.USER_ID_FOR_SRP || email;

    // Ensure MFA screen can recover even if router state is lost (refresh/new tab).
    try {
      if (session) sessionStorage.setItem(CHALLENGE_SESSION_STORAGE_KEYS.SESSION, String(session));
      if (usernameForChallenge) {
        sessionStorage.setItem(CHALLENGE_SESSION_STORAGE_KEYS.USERNAME, String(usernameForChallenge));
      }
      if (challenge) {
        sessionStorage.setItem(CHALLENGE_SESSION_STORAGE_KEYS.CHALLENGE_NAME, String(challenge));
      }
    } catch (_) {
      // ignore
    }

    // Pass session, username, and challengeName to MFA page
    yield* stopSignInAndRedirect(ROUTES.MFA_VERIFICATION, {
      session,
      username: usernameForChallenge,
      challengeName: challenge,
      // Optionally pass delivery info for EMAIL_OTP
      deliveryMedium: challengeParameters?.CODE_DELIVERY_DELIVERY_MEDIUM,
      destination: challengeParameters?.CODE_DELIVERY_DESTINATION,
    });
    return { handled: true };
  }

  const err = cognitoResult?.error;
  const wasHandled = yield* handleCognitoSignInFailure({
    email,
    err,
    stopSignInAndRedirect,
  });
  if (wasHandled) return { handled: true };

  const errorMessage = normalizeCognitoSignInErrorMessage(err);

  return { handled: false, errorMessage };
}
